/**
 * A panel that floats over the page and can be dragged out of the way (PLAN.md decision 6.8).
 *
 * Deliberately not a dialog: nothing behind it is blocked or dimmed, because the whole point is to
 * watch the charts while editing. It remembers where it was put, stays on screen when the window is
 * resized, and can be moved from the keyboard as well as the mouse.
 */

import { GripHorizontal, Minus, Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Point = { x: number; y: number };

const MARGIN = 8;
const STEP = 24; // how far an arrow key nudges it

export function DraggablePanel({
  title,
  storageKey,
  onClose,
  children,
  width = 460,
}: {
  title: string;
  /** Where this panel's position is remembered, per browser. */
  storageKey: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  const [position, setPosition] = useState<Point>(() => remembered(storageKey, width));
  const [collapsed, setCollapsed] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const grabbed = useRef<Point | null>(null);

  const move = useCallback(
    (next: Point) => {
      const box = panel.current?.getBoundingClientRect();
      const limits = {
        x: Math.max(MARGIN, window.innerWidth - (box?.width ?? width) - MARGIN),
        y: Math.max(MARGIN, window.innerHeight - 56), // keep the title bar reachable
      };
      setPosition({
        x: Math.min(Math.max(next.x, MARGIN), limits.x),
        y: Math.min(Math.max(next.y, MARGIN), limits.y),
      });
    },
    [width],
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(position));
    } catch {
      /* a forgotten position is no reason to break the panel */
    }
  }, [storageKey, position]);

  // A window that shrinks must not leave the panel off screen.
  useEffect(() => {
    const onResize = () => move(position);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [move, position]);

  useEffect(() => {
    function onPointerMove(event: PointerEvent) {
      if (!grabbed.current) return;
      move({ x: event.clientX - grabbed.current.x, y: event.clientY - grabbed.current.y });
    }
    function onPointerUp() {
      grabbed.current = null;
    }
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [move]);

  function onKeyDown(event: React.KeyboardEvent) {
    const nudges: Record<string, Point> = {
      ArrowLeft: { x: -STEP, y: 0 },
      ArrowRight: { x: STEP, y: 0 },
      ArrowUp: { x: 0, y: -STEP },
      ArrowDown: { x: 0, y: STEP },
    };
    const nudge = nudges[event.key];
    if (!nudge) return;
    event.preventDefault();
    move({ x: position.x + nudge.x, y: position.y + nudge.y });
  }

  // Through a portal to the body: an ancestor with a backdrop filter (the Result page has one) makes
  // itself the containing block for `fixed`, and the panel would then be positioned against that
  // section rather than the window - which is how it ended up half off screen the first time.
  return createPortal(
    <div
      ref={panel}
      role="region"
      aria-label={title}
      className="fixed z-40 flex max-h-[85vh] flex-col rounded-xl border border-white/15 bg-[#16181d] shadow-2xl shadow-black/60"
      style={{ left: position.x, top: position.y, width }}
    >
      <div
        className={cn(
          "flex cursor-grab touch-none items-center gap-2 rounded-t-xl border-b border-white/10 bg-white/[0.04] px-3 py-2",
          "active:cursor-grabbing",
        )}
        onPointerDown={(event) => {
          const box = panel.current!.getBoundingClientRect();
          grabbed.current = { x: event.clientX - box.left, y: event.clientY - box.top };
        }}
      >
        <button
          type="button"
          aria-label={`Move ${title} (use the arrow keys)`}
          onKeyDown={onKeyDown}
          className="cursor-grab text-muted-foreground hover:text-foreground"
        >
          <GripHorizontal className="size-4" aria-hidden="true" />
        </button>
        <h2 className="flex-1 truncate text-sm font-medium">{title}</h2>
        <Button size="xs" variant="ghost" aria-label={collapsed ? "Expand" : "Collapse"}
                onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <Plus aria-hidden="true" /> : <Minus aria-hidden="true" />}
        </Button>
        <Button size="xs" variant="ghost" aria-label={`Close ${title}`} onClick={onClose}>
          <X aria-hidden="true" />
        </Button>
      </div>
      {!collapsed && <div className="overflow-y-auto px-3 py-3">{children}</div>}
    </div>,
    document.body,
  );
}

function remembered(key: string, width: number): Point {
  const fallback = { x: Math.max(MARGIN, window.innerWidth - width - 32), y: 96 };
  try {
    const saved = window.localStorage.getItem(key);
    if (!saved) return fallback;
    const point = JSON.parse(saved) as Point;
    if (typeof point?.x !== "number" || typeof point?.y !== "number") return fallback;
    // A position from a bigger screen would leave it off this one.
    return {
      x: Math.min(Math.max(point.x, MARGIN), Math.max(MARGIN, window.innerWidth - width - MARGIN)),
      y: Math.min(Math.max(point.y, MARGIN), Math.max(MARGIN, window.innerHeight - 56)),
    };
  } catch {
    return fallback;
  }
}
